import { CARD_FORMATS } from '@/config/addUserFlow';
import { staticDefaults } from '@/config/static/catalog';
import { normalizeMentionText } from '@/utils/atMention';
import { parseNameFromEmail, normalizeEmail } from '@/config/authAllowlist';
import type { UserDraft } from '@/types/userCreation';
import { isProvisionedUserGroup, type ProvisionedUserGroup } from '@/types/provisionedUser';

const EMAIL_IN_TEXT = /[\w.+-]+@[\w.-]+\.\w{2,}/i;

const USER_INTENT_VERB = /(?:add|create|new|start)/i;
const USER_INTENT_NOUN = /(?:user|cardholder|person|employee)/i;

/** Optional @user token — not required */
const CREATE_AT_USER_PATTERN =
  /^(?:add|create|new|start)\s+(?:(?:a\s+)?(?:new\s+)?)?(?:(?:user)\s+)?['"]?@user['"]?\s+(.+)$/i;

const USER_NOUN_PAYLOAD_PATTERN =
  /^(?:add|create|new|start)\s+(?:(?:a\s+)?(?:new\s+)?)?(?:(?:user|cardholder|person|employee))\s+(.+)$/i;

const TRAILING_USER_BOILERPLATE =
  /\s+with(?:\s+a)?\s+(?:card|credential)(?:\s+for\s+this\s+site)?\.?$/i;

function extractPayloadAfterUserNoun(text: string): string | null {
  const match = text.match(USER_NOUN_PAYLOAD_PATTERN);
  if (!match) return null;

  let rest = match[1].trim();
  rest = rest.replace(TRAILING_USER_BOILERPLATE, '').trim();
  rest = rest.replace(/\s+for\s+this\s+site\.?$/i, '').trim();

  if (!rest || isBoilerplatePayload(rest)) return null;
  return rest;
}

function titleCase(word: string): string {
  if (!word) return '';
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

function parseNameSegment(segment: string): { firstName: string; lastName: string } {
  const trimmed = segment.trim();
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: titleCase(parts[0]), lastName: '' };
  return {
    firstName: titleCase(parts[0]),
    lastName: parts.slice(1).map(titleCase).join(' '),
  };
}

function parseUserGroupSuffix(text: string): {
  namePart: string;
  userGroup?: ProvisionedUserGroup;
} {
  const asMatch = text.match(/^(.+?)\s+as\s+(Administrators|Operators|Viewers|Custom|None)\s*$/i);
  if (asMatch) {
    return {
      namePart: asMatch[1].trim(),
      userGroup: asMatch[2] as ProvisionedUserGroup,
    };
  }
  const inMatch = text.match(/^(.+?)\s+in\s+(Administrators|Operators|Viewers|Custom|None)\s*$/i);
  if (inMatch) {
    return {
      namePart: inMatch[1].trim(),
      userGroup: inMatch[2] as ProvisionedUserGroup,
    };
  }
  return { namePart: text.trim() };
}

const PAYLOAD_BOILERPLATE_ONLY =
  /^(?:with(?:\s+a)?\s+(?:card|credential))(?:\s+for\s+this\s+site)?\.?$/i;

function isBoilerplatePayload(payload: string): boolean {
  return PAYLOAD_BOILERPLATE_ONLY.test(payload.trim());
}

/** Build draft fields from free text (name, email, or "name as Operators"). */
export function parseUserCreationPayload(payload: string): Partial<UserDraft> | null {
  const { namePart, userGroup } = parseUserGroupSuffix(payload);
  if (!namePart || isBoilerplatePayload(namePart)) return null;

  const emailMatch = namePart.match(EMAIL_IN_TEXT);
  const email = emailMatch ? normalizeEmail(emailMatch[0]) : undefined;

  let firstName = '';
  let lastName = '';

  if (email) {
    const fromEmail = parseNameFromEmail(email);
    firstName = fromEmail.firstName;
    lastName = fromEmail.lastName;
    const withoutEmail = namePart.replace(emailMatch![0], '').trim();
    if (withoutEmail) {
      const explicit = parseNameSegment(withoutEmail);
      if (explicit.firstName) {
        firstName = explicit.firstName;
        lastName = explicit.lastName;
      }
    }
  } else {
    const parsed = parseNameSegment(namePart);
    firstName = parsed.firstName;
    lastName = parsed.lastName;
  }

  if (!firstName && !email) return null;

  return {
    firstName,
    lastName,
    ...(email ? { email } : {}),
    ...(userGroup && isProvisionedUserGroup(userGroup) ? { userGroup } : {}),
  };
}

function hasUserRichSignals(text: string): boolean {
  return (
    EMAIL_IN_TEXT.test(text) &&
    (/(?:card|credential|facility\s+code|assign\s+access\s+group)/i.test(text) ||
      /(?:add|create|enroll|register)\s+[A-Za-z]/i.test(text))
  );
}

export function isUserCreationMessage(raw: string): boolean {
  const text = normalizeMentionText(raw.trim());
  if (USER_INTENT_VERB.test(text) && USER_INTENT_NOUN.test(text)) return true;
  if (hasUserRichSignals(text)) return true;
  return false;
}

function normalizeCardFormat(raw: string): string | undefined {
  const trimmed = raw.trim();
  const lower = trimmed.toLowerCase();
  const match = CARD_FORMATS.find(
    (f) => f.toLowerCase() === lower || lower.includes(f.toLowerCase().split(' ')[0])
  );
  if (match) return match;
  if (/26-bit\s+standard/i.test(trimmed)) {
    return CARD_FORMATS.find((f) => /26.*standard/i.test(f)) ?? '26 bits Standard';
  }
  return trimmed || undefined;
}

function normalizeAccessGroupRef(raw: string): string {
  const trimmed = raw.trim();
  if (/^\d+$/.test(trimmed)) {
    const label = `Access group ${trimmed}`;
    return staticDefaults.user.accessGroups.some((g) => g === label) ? label : trimmed;
  }
  const exact = staticDefaults.user.accessGroups.find((g) => g.toLowerCase() === trimmed.toLowerCase());
  return exact ?? trimmed;
}

/** Parse card, facility, format, access group from natural-language user requests. */
function parseUserCreationDetailsFromText(text: string): Partial<UserDraft> {
  const details: Partial<UserDraft> = {};

  const emailMatch = text.match(EMAIL_IN_TEXT);
  if (emailMatch) details.email = normalizeEmail(emailMatch[0]);

  const addName = text.match(
    /(?:add|create|enroll|register)\s+([A-Za-z][A-Za-z'-]*(?:\s+[A-Za-z][A-Za-z'-]*)*)\s*(?:,|\s+email\b)/i
  );
  if (addName) {
    const { firstName, lastName } = parseNameSegment(addName[1]);
    details.firstName = firstName;
    details.lastName = lastName;
  }

  const cardMatch =
    text.match(/(?:card|credential)(?:\s+number)?\s+(\d+)/i) ??
    text.match(/\bnumber\s+(\d{4,})\b/i);
  if (cardMatch) {
    details.cardNumber = cardMatch[1];
    details.credentialType = 'card';
  }

  const facilityMatch = text.match(/facility\s+code\s+(\d+)/i);
  if (facilityMatch) details.facilityCode = facilityMatch[1];

  const formatMatch = text.match(
    /(?:format\s+)?((?:\d+-bit\s+Standard)|(?:\d+\s+bits\s+Standard)|(?:\d+-bit\s+Wiegand\s*\([^)]+\)))/i
  );
  if (formatMatch) {
    const normalized = normalizeCardFormat(formatMatch[1]);
    if (normalized) details.cardFormat = normalized;
  }

  const groupMatch = text.match(/(?:assign\s+)?access\s+group\s+(\d+|[A-Za-z0-9\s]+?)(?:\s*$|\.|,)/i);
  if (groupMatch) {
    details.accessGroups = [normalizeAccessGroupRef(groupMatch[1])];
  }

  return details;
}

function mergeUserSeedParts(
  ...parts: Array<Partial<UserDraft> | null | undefined>
): Partial<UserDraft> | null {
  const merged: Partial<UserDraft> = {};
  for (const part of parts) {
    if (!part) continue;
    Object.assign(merged, part);
  }
  const hasValue =
    merged.firstName ||
    merged.lastName ||
    merged.email ||
    merged.cardNumber ||
    merged.facilityCode ||
    merged.cardFormat ||
    (merged.accessGroups?.length ?? 0) > 0;
  return hasValue ? merged : null;
}

/**
 * Extract seed draft from a user-creation message. @user is optional.
 * Examples:
 * - create @user rajkumar
 * - create user rajkumar
 * - create user name@company.com
 * - add a new cardholder Jane Doe
 */
export function parseUserCreationPrefillFromMessage(raw: string): Partial<UserDraft> | null {
  const text = normalizeMentionText(raw.trim());
  if (!isUserCreationMessage(text)) return null;

  const rich = parseUserCreationDetailsFromText(text);

  const atUser = text.match(CREATE_AT_USER_PATTERN);
  if (atUser) return mergeUserSeedParts(parseUserCreationPayload(atUser[1]), rich);

  const payloadAfterUser = extractPayloadAfterUserNoun(text);
  if (payloadAfterUser) return mergeUserSeedParts(parseUserCreationPayload(payloadAfterUser), rich);

  const emailMatch = text.match(EMAIL_IN_TEXT);
  if (emailMatch) {
    const email = normalizeEmail(emailMatch[0]);
    const { firstName, lastName } = parseNameFromEmail(email);
    return mergeUserSeedParts({ firstName, lastName, email }, rich);
  }

  return mergeUserSeedParts(rich);
}

/** Whether the message includes parseable name/email prefill data (@user optional). */
export function hasUserCreationPrefill(raw: string): boolean {
  const draft = parseUserCreationPrefillFromMessage(raw);
  return Boolean(draft && (draft.firstName || draft.email));
}

/** @deprecated Use parseUserCreationPrefillFromMessage */
export const parseUserCreationSeedFromMessage = parseUserCreationPrefillFromMessage;

/** @deprecated Use hasUserCreationPrefill */
export const hasUserCreationSeed = hasUserCreationPrefill;
